import styled from '@emotion/styled'

import {
  DashboardAddButton,
  DashboardPageContainer,
  SectionHeading,
  mobileGutter,
} from '../../dashboards/dashboardStyles'
import { rowNameButton } from '../../../components/DataTable'
import { roundedCardBase } from '../../../components/RoundedCard'
import breakpoints, { MOBILE } from '../../../guided-toolbox/utils/breakpoints'

// Below this the balance and the chart can no longer share a row: the year
// labels and help need room the bars would otherwise have, squeezing the bars
// down to slivers. The chart takes the full content width instead.
const NARROW_HEADER = `@media (max-width:${breakpoints.md}px)`

const PageContainer = styled(DashboardPageContainer)`
  gap: 20px;

  ${MOBILE} {
    padding: 12px 0;
    gap: 0;
  }
`

// Desktop keeps the balance on the left and the projections chart beside it; mobile
// stacks the title, balance and add button, with the projections in their own section.
const HeaderSection = styled.div`
  ${mobileGutter};
  display: grid;
  grid-template-columns: minmax(200px, max-content) minmax(0, 1fr);
  grid-template-areas:
    'header  header'
    'balance chart';
  align-items: start;
  column-gap: 40px;
  row-gap: 30px;
  padding-bottom: 6px;

  ${NARROW_HEADER} {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'header'
      'balance'
      'chart';
  }

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'title'
      'balance'
      'add';
    justify-items: center;
    row-gap: 10px;
    padding-bottom: 30px;
  }
`

// The title and add button share the top row on desktop. On mobile they drop into
// the section's own grid so the balance can sit between them.
const HeaderTop = styled.div`
  grid-area: header;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;

  ${MOBILE} {
    display: contents;
  }
`

const PageTitle = styled.h1`
  grid-area: title;
  margin: 0;
  color: ${p => p.theme.mediumGray};
  font-size: 40px;
  font-weight: 300;
  line-height: 1;

  ${MOBILE} {
    font-size: 30px;
    line-height: 1.3;
    text-align: center;
  }
`

const BalanceGroup = styled.div`
  grid-area: balance;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${MOBILE} {
    align-items: center;
  }
`

const AddAccountButtonCell = styled.div`
  grid-area: add;

  ${MOBILE} {
    justify-self: stretch;
    width: 100%;
  }
`

const ProjectionsCell = styled.div`
  grid-area: chart;
  align-self: start;
  min-width: 0;
`

const BalanceLabel = styled.div`
  color: ${p => p.theme.mediumGray};
  font-size: 18px;
  line-height: 24px;
`

const TotalBalance = styled.div`
  color: ${p => p.theme.darkestGray};
  font-size: 40px;
  font-weight: 600;
  line-height: 1.25;
`

const TotalDollarSign = styled.span`
  color: #4d9927;
`

const SectionCard = styled.section`
  ${roundedCardBase};
  display: flex;
  flex-direction: column;
  padding: 12px 20px 18px;

  ${MOBILE} {
    padding: 0;
    border: none;
    border-radius: 0;
  }
`

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;

  ${MOBILE} {
    padding: 15px 20px;
    background-color: ${p => p.theme.cardHoverBgColor ?? '#f5fafc'};
  }
`

// Keep the icon aligned with the heading without increasing the line height.
const SectionHeadingHelp = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  margin-left: 10px;
  vertical-align: bottom;
  transform: translateY(2px);
`

// Keep the help icon with the heading's last word so it cannot wrap by itself.
const SectionHeadingTail = styled.span`
  white-space: nowrap;
`

const SectionFootnote = styled.div`
  color: ${p => p.theme.lightestGray};
  font-size: 10px;
  line-height: 24px;
`

const SectionRows = styled.div`
  ${mobileGutter};
  display: flex;
  flex-direction: column;

  & > * + * {
    border-top: 2px solid ${p => p.theme.primaryLight ?? '#f0f4f5'};
  }

  ${MOBILE} {
    padding-bottom: 10px;
  }
`

const SectionEmptyMessage = styled.div`
  ${mobileGutter};
  padding-top: 25px;
  color: ${p => p.theme.mediumGray};
  font-size: 18px;
  line-height: 24px;

  ${MOBILE} {
    padding-top: 20px;
    padding-bottom: 30px;
  }
`

// Render outside SectionRows so the notice does not receive a row divider.
const SectionNotice = styled.div`
  ${mobileGutter};
  padding-top: 12px;
  color: ${p => p.theme.lightestGray};
  font-size: 16px;
  line-height: 24px;

  ${MOBILE} {
    padding-bottom: 10px;
  }
`

const SectionBalance = styled.div`
  color: ${p => p.theme.darkestGray};
  font-size: 18px;
  line-height: 24px;
  text-align: right;
`

const DollarSign = styled.span`
  color: ${p => p.theme.lightestGray};
`

// Keep account metadata aligned across rows while allowing the name column to shrink.
const SectionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(min-content, 1fr) minmax(0, 4fr) auto;
  grid-template-areas:
    'name name balance'
    'owner meta .';
  align-items: start;
  column-gap: 12px;
  padding: 19px 0;

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr) 0 auto;
    grid-template-areas:
      'name meta balance'
      'owner meta balance';
  }
`

// Let the name and owner participate directly in SectionRow's grid.
const NameField = styled.div`
  display: contents;
`

const SectionLabel = styled.div`
  grid-area: name;
  display: flex;
  align-items: center;
  min-width: 0;
  color: ${p => p.theme.mediumGray};
  font-size: 18px;
  line-height: 24px;
`

const AccountNameLink = styled.button`
  ${rowNameButton};

  ${MOBILE} {
    /* Keep markers visible by truncating only the account-name button. */
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`

const NameMarker = styled.span`
  flex: none;
`

// Show the link icon only on mobile; desktop shows the refresh details.
const MobileLinkIcon = styled.span`
  display: none;

  ${MOBILE} {
    display: inline-flex;
    flex: none;
    align-items: center;
    margin-left: 6px;
    color: ${p => p.theme.lightestGray};
  }
`

const SectionSubLabel = styled.div`
  grid-area: owner;
  color: ${p => p.theme.lightestGray};
  font-size: 14px;
  line-height: 24px;
`

const LinkedAccountMeta = styled.div`
  grid-area: meta;
  color: ${p => p.theme.lightestGray};
  font-size: 14px;
  line-height: 24px;

  > svg {
    margin-right: 6px;
    vertical-align: middle;
  }

  ${MOBILE} {
    display: none;
  }
`

// Reserve a consistent width so balances and account metadata align across rows.
const BalanceField = styled.div`
  grid-area: balance;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 145px;
  gap: 10px;
  height: 24px;

  ${MOBILE} {
    align-self: center;
    height: auto;
  }
`

const SVGContainer = styled.div`
  padding-right: 8px;
`

const WarningContainer = styled.div`
  padding-bottom: 19px;
`

const WarningMessage = styled.div`
  display: flex;
  align-items: center;
  color: #f48024;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  line-height: 22px;
`

const ReviewButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${p => p.theme.primaryColor};
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
`

/* Projections chart ------------------------------------------------------- */

// Space between a bar and the label that follows it, and the inset that keeps a
// value from touching the bar's rounded end.
const PROJECTIONS_LABEL_GAP = '20px'
const PROJECTIONS_VALUE_INSET = 12

// Two bar rows. Column 1 holds the bars and everything that trails them, so each
// label sits directly after its own bar. Column 2 reserves room for whatever
// overflows a full-width bar, which is why those rows also render a hidden copy
// of their trailing content there.
const ProjectionsChart = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  row-gap: 16px;

  ${MOBILE} {
    /* Bars run to the screen edge, so only the right side needs a gutter. */
    padding-right: 40px;
    row-gap: ${p => (p.zeroCase ? '20px' : '12px')};
    /* Carry the gap to the section that follows, unless the "why" caption is
       there to sit last and carry it instead. */
    padding-bottom: ${p => (p.zeroCase ? '0' : '40px')};
  }
`

// The bar plus its trailing labels. The labels are allowed to overflow into the
// reserved column so each one sits directly after its own bar.
const ProjectionsTrack = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`

const ProjectionsReserve = styled.div`
  display: flex;
  align-items: center;
  visibility: hidden;
`

const ProjectionsBar = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: none;
  align-items: center;
  justify-content: flex-end;
  height: 32px;
  padding: 0 ${PROJECTIONS_VALUE_INSET}px;
  border: 1px solid ${p => p.theme.primaryLighter ?? '#d4ebf2'};
  border-radius: 10px;
  background-color: ${p =>
    p.accent ? (p.theme.primaryLighter ?? '#d4ebf2') : (p.theme.primaryLight ?? '#f0f4f5')};

  ${MOBILE} {
    /* The bars start at the edge of the screen, so square off the end that meets
       it — a rounded corner and a left border both read as a gap. */
    border-left: none;
    border-radius: 0 10px 10px 0;
  }
`

const ProjectionsValue = styled.span`
  color: ${p => (p.accent ? (p.theme.primaryButtonHoverColor ?? '#02769d') : p.theme.darkestGray)};
  font-size: 18px;
  font-weight: ${p => (p.accent ? 700 : 400)};
  line-height: 24px;
  white-space: nowrap;
`

// The current plan lightens its dollar sign the way the account rows do; the
// recommended plan stays one colour throughout.
const ProjectionsDollarSign = styled.span`
  color: ${p => (p.accent ? (p.theme.primaryButtonHoverColor ?? '#02769d') : p.theme.lightestGray)};
`

const ProjectionsOutsideValue = styled(ProjectionsValue)`
  flex: none;
  margin-left: ${PROJECTIONS_LABEL_GAP};
`

const ProjectionsYear = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  margin-left: ${PROJECTIONS_LABEL_GAP};
  color: ${p => (p.accent ? p.theme.primaryColor : p.theme.lightestGray)};
  font-size: 14px;
  line-height: 24px;
  white-space: nowrap;
`

const ProjectionsYearHelp = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  margin-left: 8px;
`

// Stands in for the help icon in the hidden copy of the year label.
const ProjectionsYearHelpSpacer = styled.span`
  width: 24px;
  margin-left: 8px;
`

// HelpIcon's label is 14px by default; the design uses 12px for this caption.
const ProjectionsWhy = styled.div`
  display: flex;
  align-items: center;
  /* Sits further from the year label than the label does from its bar. */
  margin-left: 30px;
  color: ${p => p.theme.lightestGray};

  /* HelpIcon lays its label and icon out inline, so the 12px text is aligned by
     baseline against a 24px icon and rides up off the row's centre. Centre the
     two against each other instead, the way the year label does. */
  & > div > span {
    display: flex;
    align-items: center;
  }

  & > div > span > span {
    font-size: 12px;
    line-height: 24px;
  }

  ${MOBILE} {
    /* Sits last in the section on mobile, so it carries the gap to what follows. */
    margin-left: 0;
    padding: 24px 20px 40px;
  }
`

const ProjectionsFootnote = styled(SectionFootnote)`
  padding-top: 6px;
`

// Mirrors the current/recommended legend used by step-by-step advice.
const ProjectionsLegend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 25px;
  padding: 20px 20px 30px;
`

const ProjectionsLegendItem = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
  color: ${p => (p.accent ? p.theme.primaryColor : p.theme.mediumGray)};
  font-size: 14px;
`

const ProjectionsSwatch = styled.span`
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  border-radius: 50%;
  background-color: ${p => (p.accent ? p.theme.primaryColor : p.theme.lightestGray)};
`

export {
  PROJECTIONS_VALUE_INSET,
  PageContainer,
  PageTitle,
  HeaderTop,
  AddAccountButtonCell,
  ProjectionsCell,
  BalanceGroup,
  BalanceLabel,
  TotalBalance,
  TotalDollarSign,
  DashboardAddButton as AddAccountButton,
  HeaderSection,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionHeadingHelp,
  SectionHeadingTail,
  SectionFootnote,
  SectionRows,
  SectionEmptyMessage,
  SectionNotice,
  SectionBalance,
  DollarSign,
  SectionRow,
  SectionLabel,
  AccountNameLink,
  NameMarker,
  MobileLinkIcon,
  SectionSubLabel,
  NameField,
  LinkedAccountMeta,
  BalanceField,
  SVGContainer,
  WarningContainer,
  WarningMessage,
  ReviewButton,
  ProjectionsChart,
  ProjectionsTrack,
  ProjectionsReserve,
  ProjectionsBar,
  ProjectionsValue,
  ProjectionsDollarSign,
  ProjectionsOutsideValue,
  ProjectionsYear,
  ProjectionsYearHelp,
  ProjectionsYearHelpSpacer,
  ProjectionsWhy,
  ProjectionsFootnote,
  ProjectionsLegend,
  ProjectionsLegendItem,
  ProjectionsSwatch,
}
